import React, { useState, useEffect, useRef, useCallback } from 'react';
import './gallery.css';
import img0  from '../../Assets/Gallery/0.webp';
import img1  from '../../Assets/Gallery/1.webp';
import img2  from '../../Assets/Gallery/2.webp';
import img3  from '../../Assets/Gallery/3.webp';
import img4  from '../../Assets/Gallery/4.webp';
import img5  from '../../Assets/Gallery/5.webp';
import img6  from '../../Assets/Gallery/6.webp';
import img7  from '../../Assets/Gallery/7.webp';
import img8  from '../../Assets/Gallery/8.webp';
import img9  from '../../Assets/Gallery/9.webp';
import img10 from '../../Assets/Gallery/10.webp';
import img11 from '../../Assets/Gallery/11.webp';

/* ── DATA — assign categories to each image ── */
const IMAGES = [
  { src: img0,  alt: 'Shree Ganesha',    category: 'Shree Ganesha'},/*Product Photography*/
  { src: img1,  alt: 'Architecturural Photography',   category: 'Architectural' },/*Architectural Photography*/
  { src: img2,  alt: 'Wedding Candid',  category: 'Candid'       },
  { src: img3,  alt: 'Pre-Wedding Shoot',   category: 'Pre-Wedding'      },
  { src: img4,  alt: 'Wedding Moments',category: 'Wedding'      },
  { src: img5,  alt: 'Haldi Ceremony',    category: 'Wedding'      },
  { src: img6,  alt: 'Pre-Wedding shoot',   category: 'Pre-Wedding'  },
  { src: img7,  alt: 'Wedding Precious Moments',         category: 'Wedding'      },
  { src: img8,  alt: 'Wedding Golden Groom',  category: 'Wedding'      },
  { src: img9,  alt: 'Architectural Shoot',      category: 'Architectural'},
  { src: img10, alt: 'Wedding Cinematography', category: 'Wedding'      },/*Wedding Cinematography*/
  { src: img11, alt: 'Engagement Groom Moments',   category: 'Engagement'   },/*Engagement Shoot*/
];

const CATEGORIES = ['All', ...Array.from(new Set(IMAGES.map(i => i.category)))];

const Gallery = () => {
  const [active,       setActive]       = useState(0);       // current carousel slide
  const [paused,       setPaused]       = useState(false);   // pause on hover
  const [filterCat,    setFilterCat]    = useState('All');   // active category
  const [dragStart,    setDragStart]    = useState(null);
  const intervalRef = useRef(null);
  const trackRef    = useRef(null);

  const filtered = filterCat === 'All'
    ? IMAGES
    : IMAGES.filter(i => i.category === filterCat);

  /* Clamp active when filter changes */
  useEffect(() => {
    setActive(0);
  }, [filterCat]);

  /* Auto-advance */
  const startTimer = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!paused) setActive(a => (a + 1) % filtered.length);
    }, 3500);
  }, [paused, filtered.length]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(intervalRef.current);
  }, [startTimer]);

  const goTo = (idx) => {
    setActive((idx + filtered.length) % filtered.length);
    startTimer();
  };



  /* Touch swipe on carousel */
  const onTouchStart = (e) => setDragStart(e.touches[0].clientX);
  const onTouchEnd   = (e) => {
    if (dragStart === null) return;
    const delta = dragStart - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) goTo(active + (delta > 0 ? 1 : -1));
    setDragStart(null);
  };

  return (
    <section id="Gallery" className="gallery-section">

      {/* ── HEADER ── */}
      <div className="gallery-header">
        <span className="gallery-eyebrow">Our Work</span>
        <h2 className="gallery-title">Gallery</h2>
        <div className="gallery-ornament">
          <span /><svg width="14" height="14" viewBox="0 0 24 24" fill="#C9A84C"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4z"/></svg><span />
        </div>
      </div>

      {/* ── CATEGORY FILTERS ── */}
      <div className="gallery-filters" role="tablist" aria-label="Filter gallery by category">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            role="tab"
            aria-selected={filterCat === cat}
            className={`gallery-filter-btn ${filterCat === cat ? 'gallery-filter-btn--active' : ''}`}
            onClick={() => setFilterCat(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* ── CAROUSEL ── */}
      <div
        className="gallery-track"
        ref={trackRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Slides */}
        <div
          className="gallery-strip"
          style={{ transform: `translateX(${-active * 100}%)` }}
        >
          {filtered.map((img, i) => (
            <div
              key={img.src + i}
              className="gallery-slide"
            >
              <img src={img.src} alt={img.alt} className="gallery-slide-img" loading="lazy" />

              {/* Slide overlay */}
              <div className="gallery-slide-overlay">
                <span className="gallery-slide-cat">{img.category}</span>
                <span className="gallery-slide-label">{img.alt}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Prev / Next arrows */}
        <button className="gallery-arrow gallery-arrow--left"  onClick={() => goTo(active - 1)} aria-label="Previous image">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <button className="gallery-arrow gallery-arrow--right" onClick={() => goTo(active + 1)} aria-label="Next image">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>

        {/* Progress bar */}
        <div className="gallery-progress-bar">
          <div
            className="gallery-progress-fill"
            key={`${active}-${filterCat}`}
            style={{ animationDuration: paused ? '0s' : '3.5s' }}
          />
        </div>
      </div>

      {/* ── DOT NAVIGATION ── */}
      <div className="gallery-dots" role="tablist" aria-label="Slide navigation">
        {filtered.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === active}
            className={`gallery-dot ${i === active ? 'gallery-dot--active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* ── THUMBNAIL STRIP ── */}
      <div className="gallery-thumbs">
        {filtered.map((img, i) => (
          <button
            key={img.src + i}
            className={`gallery-thumb ${i === active ? 'gallery-thumb--active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={img.alt}
          >
            <img src={img.src} alt={img.alt} loading="lazy" />
          </button>
        ))}
      </div>



    </section>
  );
};

export default Gallery;